import { useState } from 'react';
import { Link } from 'react-router-dom';
import { brandInfo } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      return;
    }

    setFormSubmitting(true);
    try {
      const formData = new FormData(form);
      const url = 'https://readdy.ai/api/form/d9snjut8o1vn02ekuii0';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const msg = parsed?.meta?.message || parsed?.message || '';
      if (res.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else if (msg.toLowerCase().includes('spam')) {
        setFormError('提交失败，请稍后重试。');
      } else {
        setFormError(msg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormError('网络错误，请稍后重试。');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 91.cn 一站式互联网服务平台"
        description="91.cn是国内领先的一站式互联网服务平台，自2010年成立以来，已服务超过10万家企业客户。了解我们的品牌历程、核心价值观和联系方式。"
        keywords="91.cn,关于91.cn,91.cn联系方式,91.cn品牌介绍,互联网服务平台"
        canonicalUrl="https://www.91.cn/about"
      />
      <main>
        <div className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20abstract%20office%20teamwork%20concept%2C%20warm%20coral%20and%20teal%20gradients%2C%20clean%20soft%20geometric%20shapes%2C%20professional%20technology%20atmosphere%2C%20diffused%20warm%20lighting%2C%20editorial%20quality%2C%20no%20text&width=1600&height=900&seq=about-hero&orientation=landscape"
            alt="关于 91.cn"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              关于 91.cn
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-lg">
              让每个企业都能轻松拥抱互联网
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
              品牌介绍
            </h2>
            <div className="flex flex-col gap-4">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {brandInfo.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
              <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600">{brandInfo.mission}</p>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600">{brandInfo.vision}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
              核心价值观
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              驱动我们不断前行的信念
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {brandInfo.values.map((v, i) => (
                <div
                  key={v}
                  className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="font-heading font-bold text-lg text-accent-600">{i + 1}</span>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm">{v}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
              发展历程
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              从 2010 到 2026，每一步都扎实有力
            </p>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {brandInfo.milestones.map((m, i) => (
                  <div
                    key={m.year}
                    className={`relative flex items-start gap-6 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                      <div className={`hidden md:block ${i % 2 === 0 ? 'md:pr-10' : 'md:pl-10'}`}>
                        <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                          {m.year}
                        </span>
                        <p className="text-sm text-foreground-700">{m.event}</p>
                      </div>
                      <div className="md:hidden pl-2">
                        <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                          {m.year}
                        </span>
                        <p className="text-sm text-foreground-700">{m.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-[10px] md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              期待与您的合作
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-phone-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1 text-sm">电话咨询</h3>
                <a href="tel:400-810-9191" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  400-810-9191
                </a>
                <p className="text-xs text-foreground-400 mt-1">{brandInfo.contact.workingHours}</p>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1 text-sm">电子邮件</h3>
                <a href="mailto:service@91.cn" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  service@91.cn
                </a>
                <p className="text-xs text-foreground-400 mt-1">24 小时内回复</p>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1 text-sm">公司地址</h3>
                <p className="text-sm text-foreground-600">{brandInfo.contact.address}</p>
              </div>
            </div>

            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>

              {formSuccess ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-foreground-900 mb-2">消息已发送！</h4>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快与您联系。</p>
                  <button
                    onClick={() => setFormSuccess(false)}
                    className="mt-5 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="about-subject"
                      name="subject"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多 500 个字符</p>
                  </div>

                  <div className="form-hp-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  {formError && (
                    <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 py-3 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {formSubmitting ? '发送中...' : '发送消息'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}